import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import emailjs from 'emailjs-com';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const cardRef = useRef(null);
  const btnRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error on change
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      // Shake the card on validation failure
      gsap.to(cardRef.current, {
        x: [-10, 10, -8, 8, -4, 4, 0],
        duration: 0.5,
        ease: 'power2.out',
      });
      toast.error('Please fill all fields correctly!', {
        position: 'bottom-right',
        theme: 'dark',
      });
      return;
    }

    setSending(true);

    // Button loading animation
    gsap.to(btnRef.current, {
      scale: 0.95,
      duration: 0.2,
    });

    // Replace with your EmailJS credentials
    emailjs
      .send(
        'YOUR_SERVICE_ID',
        'YOUR_TEMPLATE_ID',
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        'YOUR_USER_ID'
      )
      .then(() => {
        setSending(false);
        setSent(true);

        // Success animation on button
        gsap.to(btnRef.current, {
          scale: 1,
          duration: 0.3,
        });

        // Card glow pulse
        gsap.fromTo(cardRef.current, {
          boxShadow: '0 0 0px rgba(30, 205, 151, 0)',
        }, {
          boxShadow: '0 0 60px rgba(30, 205, 151, 0.3), 0 0 120px rgba(30, 205, 151, 0.1)',
          duration: 0.6,
          yoyo: true,
          repeat: 1,
        });

        toast.success('🚀 Message sent successfully!', {
          position: 'bottom-right',
          theme: 'dark',
        });

        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSent(false), 3000);
      })
      .catch(() => {
        setSending(false);
        gsap.to(btnRef.current, {
          scale: 1,
          duration: 0.3,
        });
        toast.error('Failed to send message. Please try again.', {
          position: 'bottom-right',
          theme: 'dark',
        });
      });
  };

  useEffect(() => {
    const section = sectionRef.current;
    const card = cardRef.current;

    // Section reveal - 3D perspective entrance
    gsap.from(card, {
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        end: 'top 20%',
        scrub: 1,
      },
      y: 150,
      rotateX: 15,
      scale: 0.85,
      opacity: 0,
      transformPerspective: 1000,
    });

    // Header animations
    gsap.from('.contact-badge', {
      scrollTrigger: {
        trigger: section,
        start: 'top 85%',
        end: 'top 55%',
        scrub: 1,
      },
      y: 40,
      opacity: 0,
      scale: 0.7,
    });

    gsap.from('.contact-title', {
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        end: 'top 45%',
        scrub: 1,
      },
      y: 80,
      opacity: 0,
      scale: 0.9,
    });

    gsap.from('.contact-subtitle', {
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        end: 'top 40%',
        scrub: 1,
      },
      y: 40,
      opacity: 0,
    });

    // Info items stagger animation
    const infoItems = gsap.utils.toArray('.contact-info-item');
    if (infoItems.length) {
      gsap.fromTo(infoItems,
        { y: 40, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.12,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.contact-info-row',
            start: 'top 95%',
            toggleActions: 'play none none none',
          },
        }
      );

      // Fallback: ensure items are visible after 2s regardless of scroll
      setTimeout(() => {
        infoItems.forEach(item => {
          if (item.style.opacity === '0' || getComputedStyle(item).opacity === '0') {
            gsap.to(item, { opacity: 1, y: 0, scale: 1, duration: 0.4 });
          }
        });
      }, 2000);
    }

    // Form fields stagger with slide-in
    gsap.from('.form-group', {
      scrollTrigger: {
        trigger: formRef.current,
        start: 'top 85%',
        end: 'top 55%',
        scrub: 1,
      },
      y: 50,
      opacity: 0,
      stagger: 0.1,
    });

    // Submit button entrance
    gsap.from('.submit-btn', {
      scrollTrigger: {
        trigger: formRef.current,
        start: 'top 80%',
        end: 'top 40%',
        scrub: 1,
      },
      y: 40,
      opacity: 0,
      scale: 0.8,
    });

    // Floating orbs
    gsap.to('.contact-orb-1', {
      y: -30,
      x: 20,
      duration: 4,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    gsap.to('.contact-orb-2', {
      y: 25,
      x: -15,
      duration: 5,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    gsap.to('.contact-orb-3', {
      y: -20,
      x: -25,
      duration: 3.5,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    // Card tilt on mouse move
    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      gsap.to(card, {
        rotateY: x * 5,
        rotateX: -y * 5,
        duration: 0.5,
        ease: 'power2.out',
        transformPerspective: 1000,
      });
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        rotateY: 0,
        rotateX: 0,
        duration: 0.5,
        ease: 'power2.out',
      });
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    // Refresh ScrollTrigger after layout settles (fixes Lenis timing issues)
    const refreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(refreshTimeout);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <section className='contact' id='contact' ref={sectionRef}>
      <ToastContainer />

      {/* Floating orbs */}
      <div className='contact-orb contact-orb-1'></div>
      <div className='contact-orb contact-orb-2'></div>
      <div className='contact-orb contact-orb-3'></div>

      <div className='contact-content'>
        <div className='contact-header'>
          <span className='contact-badge'>
            <i className="fa-solid fa-rocket"></i> Let's Connect
          </span>
          <h2 className='contact-title'>Get In Touch</h2>
          <p className='contact-subtitle'>Have an idea? Let's bring it to life. I'm always excited to work on new and challenging projects.</p>
        </div>

        <div className='contact-info-row'>
          <div className='contact-info-item'>
            <div className='contact-info-icon'>
              <i className="fa-solid fa-envelope"></i>
            </div>
            <h4>Email</h4>
            <p>akibmulla47@gmail.com</p>
          </div>
          <div className='contact-info-item'>
            <div className='contact-info-icon'>
              <i className="fa-solid fa-location-dot"></i>
            </div>
            <h4>Location</h4>
            <p>Pune, India</p>
          </div>
          <div className='contact-info-item'>
            <div className='contact-info-icon'>
              <i className="fa-solid fa-phone"></i>
            </div>
            <h4>Phone</h4>
            <p>+91 9145576437</p>
          </div>
          <div className='contact-info-item'>
            <div className='contact-info-icon'>
              <i className="fa-solid fa-briefcase"></i>
            </div>
            <h4>Freelance</h4>
            <p>Available</p>
          </div>
        </div>

        <div className='contact-form-card' ref={cardRef}>
          <div className='form-card-glow'></div>
          <h3 className='form-card-title'>Send me a message <span className='form-emoji'>💬</span></h3>
          <form className='contact-form' ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className='form-row'>
              <div className={`form-group ${errors.name ? 'form-group-error' : ''}`}>
                <label>Name</label>
                <input
                  type='text'
                  name='name'
                  placeholder='John Doe'
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <span className='field-error'>{errors.name}</span>}
              </div>
              <div className={`form-group ${errors.email ? 'form-group-error' : ''}`}>
                <label>Email</label>
                <input
                  type='email'
                  name='email'
                  placeholder='john@example.com'
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <span className='field-error'>{errors.email}</span>}
              </div>
            </div>
            <div className={`form-group ${errors.subject ? 'form-group-error' : ''}`}>
              <label>Subject</label>
              <input
                type='text'
                name='subject'
                placeholder="Let's work together!"
                value={formData.subject}
                onChange={handleChange}
              />
              {errors.subject && <span className='field-error'>{errors.subject}</span>}
            </div>
            <div className={`form-group ${errors.message ? 'form-group-error' : ''}`}>
              <label>Message</label>
              <textarea
                name='message'
                placeholder='Tell me about your project...'
                rows='6'
                value={formData.message}
                onChange={handleChange}
              ></textarea>
              {errors.message && <span className='field-error'>{errors.message}</span>}
            </div>
            <button
              type='submit'
              className={`submit-btn ${sending ? 'submit-btn-loading' : ''} ${sent ? 'submit-btn-sent' : ''}`}
              disabled={sending}
              ref={btnRef}
            >
              <span className='submit-btn-text'>
                {sent ? '✓ Sent!' : sending ? 'Sending...' : 'Send Message'}
              </span>
              {!sent && !sending && (
                <span className='submit-btn-icon'>
                  <i className="fa-solid fa-paper-plane"></i>
                </span>
              )}
              {sending && <span className='submit-btn-spinner'></span>}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
